import { Backdrop } from '@material-ui/core'
import { FilterValuesType } from 'elements/hardwareFilter/type'
import useTrackComponent from 'hooks/eventTracking/useTrackComponent'
import isEmpty from 'lodash/isEmpty'
import objectHash from 'object-hash'
import { Button, ComponentVersions_3_0, ComponentVersionsInherit, Drawer, styled } from 'opensolar-ui'
import { useTranslate } from 'ra-core'
import React, { useEffect, useMemo } from 'react'
import { Form, useForm, useFormState } from 'react-final-form'
import { ActionStatus, ActionType, EventType } from 'types/tracking'
import FilterSection from './FilterSection'
import SuggestedFilterSection from './SuggestedFilterSection'
import { FilterConfig, FilterField, OrderOption, SortOption, SuggestedFilter } from './types'

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .OSUI-Paper-root': {
    width: 600,
    maxWidth: 600,
    [theme.breakpoints.down('sm')]: {
      width: '90%',
    },
  },
}))

const ContentWrapper = styled('div')({
  padding: '20px 20px 20px 24px',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflowY: 'auto',
})

const Header = styled('h1')({
  fontSize: 18,
  marginBottom: 30,
})

const SectionHeader = styled('h2')(({ theme }) => ({
  color: theme.palette.grey[700],
  fontSize: 12,
  fontWeight: 500,
  marginBottom: 10,
  marginTop: 0,
  textTransform: 'uppercase',
}))

const FormStyled = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
})

const ToolbarWrapper = styled('div')(({ theme }) => ({
  padding: 20,
  display: 'flex',
  marginTop: 'auto',
  borderBottomLeftRadius: 8,
  borderBottomRightRadius: 8,
  borderTop: '1px solid ' + theme.palette.secondary.light,
}))

const DiscardBtn = styled(Button)({
  marginRight: 10,
  marginLeft: 'auto',
})

const SectionDivider = styled('div')(({ theme }) => ({
  height: 1,
  backgroundColor: theme.palette.grey[200],
  margin: '16px 0',
}))

const OptionsContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
})

const OptionRow = styled('div')<{ selected?: boolean }>(({ theme, selected }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '10px 12px',
  marginBottom: 4,
  borderRadius: 6,
  fontSize: 14,
  fontWeight: 400,
  backgroundColor: selected ? theme.palette.blue[50] : 'transparent',
  color: selected ? theme.palette.blue[700] : theme.palette.grey[900],
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: selected ? theme.palette.blue[50] : theme.palette.grey[50],
  },
}))

interface PropTypes {
  open: boolean
  onClose: () => void
  filterValues: FilterValuesType
  setFilters: (filters: FilterValuesType) => void
  filterOptions: FilterConfig[]
  suggestedFilters?: SuggestedFilter[]
  sortOptions?: SortOption[]
  orderOptions?: OrderOption[]
  defaultSortBy?: string
  defaultOrderBy?: 'asc' | 'desc'
  title?: string
  trackingComponentKey?: string
  trackingEventName?: string
  disableTracking?: boolean
}

const FormStateHandler = ({ open }: { open: boolean }) => {
  const form = useForm()

  useEffect(() => {
    if (!open) {
      form.setConfig('keepDirtyOnReinitialize', false)
      form.reset()
      form.setConfig('keepDirtyOnReinitialize', true)
    }
  }, [form, open])

  return null
}

interface SortBySectionProps {
  options: SortOption[]
}

const SortBySection: React.FC<SortBySectionProps> = ({ options }) => {
  const form = useForm()
  const { values: formValues } = useFormState({ subscription: { values: true } })
  const translate = useTranslate()
  const selectedSortBy = formValues.sortBy as string | undefined

  const handleSelectSort = (field: string) => {
    const currentValue = form.getState().values.sortBy
    const sortBy = currentValue === field ? undefined : field
    form.change('sortBy', sortBy)
  }

  return (
    <OptionsContainer>
      {options.map((option) => {
        const isSelected = selectedSortBy === option.value

        return (
          <OptionRow key={option.value} selected={isSelected} onClick={() => handleSelectSort(option.value)}>
            {translate(option.label)}
          </OptionRow>
        )
      })}
    </OptionsContainer>
  )
}

interface OrderBySectionProps {
  options: OrderOption[]
}

const OrderBySection: React.FC<OrderBySectionProps> = ({ options }) => {
  const form = useForm()
  const { values: formValues } = useFormState({ subscription: { values: true } })
  const translate = useTranslate()
  const selectedOrderBy = formValues.orderBy as string | undefined

  const handleSelectOrder = (order: string) => {
    form.change('orderBy', order)
  }

  return (
    <OptionsContainer>
      {options.map((option) => {
        const isSelected = selectedOrderBy === option.value

        return (
          <OptionRow key={option.value} selected={isSelected} onClick={() => handleSelectOrder(option.value)}>
            {translate(option.label)}
          </OptionRow>
        )
      })}
    </OptionsContainer>
  )
}

export const parseFilterValues = (filterValues: FilterValuesType, filterOptions: FilterConfig[]): FilterField[] => {
  let parsedValues: FilterField[] = []
  const filterKeys = Object.keys(filterValues)
  if (!!filterKeys.length) {
    filterOptions.forEach((filter) => {
      const relatedFields = filter.relatedFields || [filter.id]
      const hasRelatedField = relatedFields.some((field) => filterKeys.includes(field))
      if (hasRelatedField) {
        const relatedValues = relatedFields.reduce((result, field) => {
          if (filterKeys.includes(field)) {
            return { ...result, [field]: filterValues[field] }
          }
          return result
        }, {})

        parsedValues.push({
          fieldId: filter.id,
          value: relatedValues,
          fields: filter.parseInitialValue ? filter.parseInitialValue(relatedValues) : undefined,
        })
      }
    })
  }
  return parsedValues
}

/**
 * FilterDrawer is adapted from the project filter drawer. It is a first pass at a more
 * generic filter drawer that can be used for any list view. It is not yet fully implemnent,
 * but it is a good starting point and we should consider extending it in future.
 */

const FilterDrawer: React.FC<PropTypes> = ({
  open,
  onClose,
  filterValues,
  setFilters,
  filterOptions,
  suggestedFilters,
  sortOptions,
  orderOptions,
  defaultSortBy,
  defaultOrderBy = 'asc',
  title = 'Apply Filters',
  trackingComponentKey = 'filter_drawer',
  trackingEventName = 'User Applied Filters',
  disableTracking = false,
}) => {
  const translate = useTranslate()
  const { trackEvent } = useTrackComponent({
    componentKey: trackingComponentKey,
    eventName: trackingEventName,
  })
  const formKey = objectHash(filterValues)

  const initialFormValues = useMemo(() => {
    const ordering = filterValues.ordering as string | undefined
    let sortBy = defaultSortBy
    let orderBy: 'asc' | 'desc' = defaultOrderBy

    if (ordering && !isEmpty(ordering)) {
      const firstField = ordering.split(',')[0]
      if (firstField) {
        sortBy = firstField.replace(/^-/, '')
        orderBy = firstField.startsWith('-') ? 'desc' : 'asc'
      }
    }

    return {
      filters: parseFilterValues(filterValues, filterOptions),
      sortBy,
      orderBy,
    }
  }, [formKey, filterOptions, defaultSortBy, defaultOrderBy])

  const handleSubmit = (values: any) => {
    // Extract new filter values from the form
    let newFilterValues = values.filters.reduce((result: any, filter: any) => {
      if (filter.value) {
        return { ...result, ...filter.value }
      }
      return result
    }, {})

    // Preserve existing search query if present
    if (filterValues.q) newFilterValues.q = filterValues.q

    // Build ordering string from separate sortBy and orderBy values
    if (sortOptions && sortOptions.length > 0) {
      const sortBy = values.sortBy as string | undefined
      const orderBy = values.orderBy as 'asc' | 'desc'

      if (sortBy) {
        const prefix = orderBy === 'desc' ? '-' : ''
        newFilterValues.ordering = `${prefix}${sortBy}`
      } else {
        newFilterValues.ordering = ''
      }
    } else if (filterValues.ordering) {
      newFilterValues.ordering = filterValues.ordering
    }

    if (!disableTracking) {
      trackEvent(
        EventType.SYSTEM_EVENT,
        { type: ActionType.SAVE, status: ActionStatus.SUCCESS },
        { filters: newFilterValues, sortBy: values.sortBy, orderBy: values.orderBy }
      )
    }

    setFilters(newFilterValues)
    onClose()
  }

  return (
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <Backdrop open={open} style={{ zIndex: 1100 }} onClick={onClose}></Backdrop>
      <StyledDrawer variant="persistent" anchor="right" open={open}>
        <Form
          key={formKey}
          initialValues={initialFormValues}
          onSubmit={handleSubmit}
          keepDirtyOnReinitialize
          render={({ handleSubmit }) => (
            <FormStyled onSubmit={handleSubmit}>
              <FormStateHandler open={open} />
              <ContentWrapper>
                <Header>{translate(title)}</Header>

                {sortOptions && sortOptions.length > 0 && (
                  <>
                    <SectionHeader>{translate('Sort By')}</SectionHeader>
                    <SortBySection options={sortOptions} />
                    <SectionDivider />
                  </>
                )}

                {orderOptions && orderOptions.length > 0 && (
                  <>
                    <SectionHeader>{translate('Order By')}</SectionHeader>
                    <OrderBySection options={orderOptions} />
                    <SectionDivider />
                  </>
                )}

                <SectionHeader>{translate('Filter')}</SectionHeader>
                <FilterSection
                  source="filters"
                  filterOptions={filterOptions}
                  trackingComponentKey={trackingComponentKey}
                  trackingEventName={trackingEventName}
                />
                <SectionHeader>{translate('Suggested Filters')}</SectionHeader>
                <SuggestedFilterSection
                  suggestedFilters={suggestedFilters}
                  trackingComponentKey={trackingComponentKey}
                  trackingEventName={trackingEventName}
                />
              </ContentWrapper>
              <ToolbarWrapper>
                <DiscardBtn variant="contained" color="default" onClick={onClose}>
                  {translate('Cancel')}
                </DiscardBtn>
                <Button variant="contained" color="primary" type="submit">
                  {translate('Confirm')}
                </Button>
              </ToolbarWrapper>
            </FormStyled>
          )}
        />
      </StyledDrawer>
    </ComponentVersionsInherit>
  )
}

export default FilterDrawer
